import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import {userInfoState} from 'utils/Atoms';
import ScreenLayout from 'components/common/ScreenLayout';
import ContainerHeader from 'components/common/ContainerHeader';
import DefaultText from 'components/common/DefaultText';
import DefaultInput from 'components/common/DefaultInput';
import DefaultImageView from 'components/common/DefaultImageView';
import Pagination from 'components/common/Pagination';
import {
  searchBackOfficeContractor,
  listHqAccessGrants,
} from 'function/api';
import {convertTotalPage} from 'function/converter';
import {formatBusinessRegisterNumber} from 'function/formatter';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';
import HqGrantAddModal from 'components/headquartersAccessControl/HqGrantAddModal';
import RevokeHqGrantModal from 'components/headquartersAccessControl/RevokeHqGrantModal';

/**
 * Phase 2.10 — 본사관리자(HQ) 접근 권한 관리 페이지.
 * - BackOffice 운영팀이 시공사별로 SEED/INVITED grant 부여/회수.
 * - 운영팀 재량 (시공사당 SEED 수 제한 없음).
 */
const HeadquartersAccessControl = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);

  // 좌측 시공사 목록 상태
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [contractorData, setContractorData] = useState<null | any>(null);

  // 우측 grant 관리 상태
  const [selectedContractor, setSelectedContractor] = useState<null | any>(
    null,
  );
  const [grantData, setGrantData] = useState<null | {items: any[]}>(null);

  // 모달
  const [addGrantModal, setAddGrantModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<null | any>(null);

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (userInfo?.accessToken) {
      fetchContractors();
    }
  }, [userInfo, currentPage]);

  useEffect(() => {
    if (selectedContractor) {
      fetchGrants(selectedContractor.uuid);
    } else {
      setGrantData(null);
    }
  }, [selectedContractor]);

  const fetchContractors = () => {
    searchBackOfficeContractor({
      userInfo,
      setErrorModal,
      setIsLoading,
      page: currentPage,
      keyword,
    }).then((result: any) => {
      if (result) {
        setContractorData(result);
        setTotalPage(convertTotalPage(result.totalCount ?? 0, 10));
      }
    });
  };

  const fetchGrants = (contractorUUID: string) => {
    listHqAccessGrants({
      userInfo,
      setErrorModal,
      setIsLoading,
      contractorUUID,
    }).then((result: any) => {
      if (result) {
        setGrantData(result);
      }
    });
  };

  const handleGrantAdded = () => {
    setAddGrantModal(false);
    if (selectedContractor) {
      fetchGrants(selectedContractor.uuid);
    }
  };

  const handleGrantRevoked = () => {
    setRevokeTarget(null);
    if (selectedContractor) {
      fetchGrants(selectedContractor.uuid);
    }
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader
          mainTitle="userManagement"
          subTitle="headquartersAccessControl"
        />

        <Inner>
          <DefaultText
            text="본사관리자 접근 권한"
            textStyle={{
              fontSize: minmax(25),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />
          <DefaultText
            text="시공사를 선택하면 우측에서 본사관리자(HQ) 접근 권한을 부여/회수할 수 있습니다. 시공사당 SEED 수 제한 없음 (운영팀 재량)."
            textStyle={{
              fontSize: minmax(16),
              color: colors.darkGray,
              marginBottom: minmax(24),
            }}
          />

          <SplitArea>
            {/* 좌측: 시공사 검색 */}
            <LeftPanel>
              <DefaultText
                text="시공사 선택"
                textStyle={{fontSize: minmax(20), fontWeight: 'bold', marginBottom: minmax(12)}}
              />

              <SearchWrap>
                <DefaultInput
                  value={keyword}
                  setValue={(e: any) => setKeyword(e)}
                  placeholderText="시공사명/연락처 검색"
                  wrapperStyle={{height: '100%'}}
                  onKeyPress={(e: any) => {
                    if (e.key === 'Enter') {
                      setCurrentPage(1);
                      fetchContractors();
                    }
                  }}
                />
                <DefaultImageView
                  source="images/common/search.png"
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchContractors();
                  }}
                />
              </SearchWrap>

              <ContractorList>
                {contractorData?.totalCount ? (
                  (contractorData.contractorList ?? []).map((c: any) => (
                    <ContractorRow
                      key={c.uuid}
                      $selected={selectedContractor?.uuid === c.uuid}
                      onClick={() => setSelectedContractor(c)}>
                      <DefaultText
                        text={c.name || '-'}
                        textStyle={{fontSize: minmax(18), fontWeight: 600}}
                      />
                      <DefaultText
                        text={formatBusinessRegisterNumber(c.businessRegisterNumber) || '-'}
                        textStyle={{fontSize: minmax(14), color: colors.darkGray}}
                      />
                      <DefaultText
                        text={c.allowState}
                        textStyle={{fontSize: minmax(12), color: colors.primary2}}
                      />
                    </ContractorRow>
                  ))
                ) : (
                  <Empty>시공사가 없습니다</Empty>
                )}
              </ContractorList>

              {contractorData?.totalCount ? (
                <Pagination
                  totalPage={totalPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              ) : null}
            </LeftPanel>

            {/* 우측: 선택된 시공사의 grant 관리 */}
            <RightPanel>
              {selectedContractor ? (
                <>
                  <PanelHeader>
                    <div>
                      <DefaultText
                        text={selectedContractor.name}
                        textStyle={{fontSize: minmax(22), fontWeight: 'bold'}}
                      />
                      <DefaultText
                        text={`UUID: ${selectedContractor.uuid}`}
                        textStyle={{fontSize: minmax(12), color: colors.darkGray, marginTop: minmax(4)}}
                      />
                    </div>
                    <AddButton onClick={() => setAddGrantModal(true)}>
                      + 권한 부여
                    </AddButton>
                  </PanelHeader>

                  <DefaultText
                    text="활성 grant 목록"
                    textStyle={{fontSize: minmax(18), fontWeight: 600, marginTop: minmax(20), marginBottom: minmax(10)}}
                  />

                  {grantData?.items && grantData.items.length > 0 ? (
                    <GrantTable>
                      <GrantHeader>
                        <DefaultText text="역할" textStyle={{flex: 0.6, fontWeight: 'bold'}} />
                        <DefaultText text="이름" textStyle={{flex: 1, fontWeight: 'bold'}} />
                        <DefaultText text="전화번호" textStyle={{flex: 1.2, fontWeight: 'bold'}} />
                        <DefaultText text="부여 시각" textStyle={{flex: 1.2, fontWeight: 'bold'}} />
                        <DefaultText text="부여자" textStyle={{flex: 1.2, fontWeight: 'bold'}} />
                        <DefaultText text="액션" textStyle={{flex: 0.6, fontWeight: 'bold'}} />
                      </GrantHeader>
                      {grantData.items.map((g: any) => (
                        <GrantRow key={g.uuid}>
                          <RoleBadge $role={g.grantRole}>{g.grantRole}</RoleBadge>
                          <DefaultText
                            text={g.userInfo?.name ?? g.userUUID?.slice(0, 8) ?? '-'}
                            textStyle={{flex: 1, fontSize: minmax(13)}}
                          />
                          <DefaultText
                            text={
                              g.userInfo?.phone
                                ? formatPhoneNumber({phone: g.userInfo.phone, locale: 'ko-KR'}) || g.userInfo.phone
                                : '-'
                            }
                            textStyle={{flex: 1.2, fontSize: minmax(13)}}
                          />
                          <DefaultText
                            text={new Date(g.grantedAt).toLocaleString('ko-KR')}
                            textStyle={{flex: 1.2, fontSize: minmax(13)}}
                          />
                          <DefaultText
                            text={g.grantedByInfo?.name ?? g.grantedByUserUUID?.slice(0, 8) ?? '-'}
                            textStyle={{flex: 1.2, fontSize: minmax(13)}}
                          />
                          <RevokeBtn onClick={() => setRevokeTarget(g)}>회수</RevokeBtn>
                        </GrantRow>
                      ))}
                    </GrantTable>
                  ) : (
                    <Empty>{'활성 grant 없음. 우측 상단 “권한 부여” 로 추가하세요.'}</Empty>
                  )}
                </>
              ) : (
                <Empty>좌측에서 시공사를 선택하세요</Empty>
              )}
            </RightPanel>
          </SplitArea>
        </Inner>
      </Container>

      {addGrantModal && selectedContractor && (
        <HqGrantAddModal
          contractor={selectedContractor}
          closeModal={() => setAddGrantModal(false)}
          onGranted={handleGrantAdded}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}

      {revokeTarget && (
        <RevokeHqGrantModal
          grant={revokeTarget}
          closeModal={() => setRevokeTarget(null)}
          onRevoked={handleGrantRevoked}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}
    </ScreenLayout>
  );
};

export default HeadquartersAccessControl;

const Container = styled.div`
  width: 100%;
  height: 100%;
`;
const Inner = styled.div`
  padding: ${minmax(20)};
`;
const SplitArea = styled.div`
  display: flex;
  gap: ${minmax(20)};
  align-items: flex-start;
`;
const LeftPanel = styled.div`
  flex: 1;
  background: ${colors.white};
  padding: ${minmax(20)};
  border-radius: ${minmax(8)};
  border: 1px solid ${colors.lightGrey};
  min-width: ${minmax(360)};
`;
const RightPanel = styled.div`
  flex: 2;
  background: ${colors.white};
  padding: ${minmax(20)};
  border-radius: ${minmax(8)};
  border: 1px solid ${colors.lightGrey};
  min-height: ${minmax(500)};
`;
const SearchWrap = styled.div`
  position: relative;
  width: 100%;
  height: ${minmax(40)};
  margin-bottom: ${minmax(12)};
`;
const ContractorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${minmax(6)};
  margin-bottom: ${minmax(12)};
`;
const ContractorRow = styled.div<{$selected?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${minmax(2)};
  padding: ${minmax(10)};
  border: 1px solid ${(p) => (p.$selected ? colors.primary : colors.lightGrey)};
  border-radius: ${minmax(6)};
  background: ${(p) => (p.$selected ? colors.whisper : colors.white)};
  cursor: pointer;
  &:hover {
    background: ${colors.whisper};
  }
`;
const Empty = styled.div`
  padding: ${minmax(40)};
  text-align: center;
  color: ${colors.darkGray};
`;
const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.lightGrey};
  padding-bottom: ${minmax(12)};
`;
const AddButton = styled.button`
  background: ${colors.primary};
  color: white;
  border: none;
  padding: ${minmax(10)} ${minmax(20)};
  border-radius: ${minmax(6)};
  cursor: pointer;
  font-size: ${minmax(15)};
  font-weight: bold;
`;
const GrantTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${minmax(4)};
`;
const GrantHeader = styled.div`
  display: flex;
  padding: ${minmax(10)} ${minmax(8)};
  background: ${colors.lightGrey};
  border-radius: ${minmax(4)};
`;
const GrantRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${minmax(10)} ${minmax(8)};
  border-bottom: 1px solid ${colors.lightGrey};
`;
const RoleBadge = styled.span<{$role: string}>`
  flex: 0.6;
  display: inline-block;
  padding: ${minmax(4)} ${minmax(10)};
  border-radius: ${minmax(4)};
  font-size: ${minmax(13)};
  font-weight: bold;
  background: ${(p) => (p.$role === 'SEED' ? colors.primary : '#888')};
  color: white;
  text-align: center;
  margin-right: ${minmax(8)};
  max-width: ${minmax(80)};
`;
const RevokeBtn = styled.button`
  flex: 0.6;
  background: #d33;
  color: white;
  border: none;
  padding: ${minmax(6)} ${minmax(12)};
  border-radius: ${minmax(4)};
  cursor: pointer;
  font-size: ${minmax(13)};
`;
